import React, { createContext, useEffect, useState } from 'react';

import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import { Popup, UiButton } from '@shared/ui';

import { AccountList } from './AccountList/AccountList';
import { AccountModal } from './AccountModal/AccountModal';
import cls from './Accounts.module.css';
import {
    fetchAccount,
    fetchArchiveAccount,
    fetchIntegrationList,
    fetchListSelect,
} from './accuntApi';
import { ArchiveAccounts } from './ArchiveAccounts/ArchiveAccounts';
import { BankIcon } from './BankIcon';
import { TableHead } from './TableHead/TableHead';

export const Context = createContext({});

export const Accounts = () => {
    const { t } = useTranslation();
    const [modalState, setModalState] = useState({
        isOpen: false,
        mode: 'add',
        currentAccount: null,
    });
    const [accounts, setAccounts] = useState([]);
    const [integrationList, setIntegrationList] = useState([]);
    const [archiveAccounts, setArchiveAccounts] = useState([]);
    const [activeMoreMenu, setActiveMoreMenu] = useState(null);
    const [isShowPopup, setIsShowPopup] = useState(false);
    const [popupMode, setPopupMode] = useState('');
    const [currentGuid, setCurrentGuid] = useState('');
    const [currentAccount, setCurrentAccount] = useState(null);
    const [popupFooterButtons, setPopupFooterButtons] = useState({
        remove: undefined,
        archive: undefined,
        unarchive: undefined,
        edit: undefined,
    });
    const [currentList, setCurrentList] = useState([]);
    const [legalList, setLegalList] = useState([]);
    const [employeelList, setEmployeeList] = useState([]);

    useEffect(() => {
        const getCurrencyUrl = routable.getRoute('getCurrencyList');
        const getEmployeeUrl = routable.getRoute('getEmployeeList');
        const getLegalUrl = routable.getRoute('getLegalList');
        const callbackCurrency = (data) => {
            const getCurrencyList = data.data.map((currency) => ({
                title: `${currency.title} (${currency.code}), ${currency.sign}`,
                value: currency.id,
                currency,
            }));
            setCurrentList(getCurrencyList);
        };
        fetchListSelect(getCurrencyUrl, callbackCurrency);

        const callbackEmployee = (data) => {
            const getEmployeeList = data.data.map((employee) => ({
                title: employee.display_name,
                value: employee.email,
                employee,
            }));
            setEmployeeList(getEmployeeList);
        };
        fetchListSelect(getEmployeeUrl, callbackEmployee);
        const callbackLegal = (data) => {
            const getLegalList = data.data.map((legal) => ({
                value: legal.guid,
                title: legal.full_name ? legal.full_name : t('Accounts.return.callbackLegal.text'),
                legal,
            }));
            setLegalList(getLegalList);
        };
        fetchListSelect(getLegalUrl, callbackLegal);

        const integrationCallback = (data) => {
            const getListIntegration = data.data.map((bankType) => ({
                value: bankType.type,
                title: bankType.label,
                icon: <BankIcon type={bankType.type} />,
                bankType,
            }));

            setIntegrationList(getListIntegration);
        };

        fetchIntegrationList(integrationCallback);
    }, []);

    const onAddAccountButton = () => {
        setModalState((prevState) => ({ ...prevState, isOpen: true, mode: 'add' }));
    };

    const [isActiveSwitcherEdit, setIsActiveSwitcherEdit] = useState(
        currentAccount ? currentAccount.integration : false
    );

    useEffect(() => {
        if (popupMode === '') {
            setPopupFooterButtons({
                remove: undefined,
                archive: undefined,
                unarchive: undefined,
            });
        }
        if (popupMode === 'remove') {
            setPopupFooterButtons({
                remove: {
                    title: t('Accounts.return.useEffect.remove.setPopupFooterButtons.title'),
                    handler: onRemoveAccount,
                },
                archive: undefined,
                unarchive: undefined,
                edit: undefined,
            });
        }
        if (popupMode === 'archive') {
            setPopupFooterButtons({
                remove: undefined,
                archive: {
                    title: t('Accounts.return.useEffect.archive.setPopupFooterButtons.title'),
                    handler: onArchiveAccount,
                },
                unarchive: undefined,
                edit: undefined,
            });
        }
        if (popupMode === 'unarchive') {
            setPopupFooterButtons({
                remove: undefined,
                archive: undefined,
                unarchive: {
                    title: t('Accounts.return.useEffect.unArchive.setPopupFooterButtons.title'),
                    handler: onUnarchiveAccount,
                },
                edit: undefined,
            });
        }
        if (popupMode === 'edit') {
            setPopupFooterButtons({
                remove: undefined,
                archive: undefined,
                unarchive: undefined,
                edit: {
                    title: t('Accounts.return.useEffect.edit.setPopupFooterButtons.title'),
                    handler: editPopupHandler,
                },
            });
        }
    }, [popupMode, currentGuid]);

    const onHideModal = () => {
        const newState = { ...modalState, isOpen: false, currentAccount: null };
        setModalState(newState);
        setCurrentGuid('');
    };

    const onHidePopup = () => {
        setIsShowPopup(false);
        if (popupMode !== 'edit') {
            setCurrentGuid('');
        }
        setIsActiveSwitcherEdit(false);
    };

    const onRemoveAccount = () => {
        const url = helpable.replaceUrlParameters(routable.getRoute('removeCompanyAccount'), {
            _guid_: currentGuid,
        });

        setIsShowPopup(false);
        loader.show();

        const isDefaultAccount = accounts.some(
            (account) => account.guid === currentGuid && account.default_account
        );

        const callBack = () => {
            setAccounts((prevState) => {
                const filteredAccounts = prevState.filter(
                    (account) => account.guid !== currentGuid
                );

                if (!filteredAccounts.length) {
                    return filteredAccounts;
                }
                const newDefaultAccount = { ...filteredAccounts[0], default_account: true };

                if (isDefaultAccount) {
                    filteredAccounts.splice(0, 1, newDefaultAccount);
                }
                return filteredAccounts;
            });

            setArchiveAccounts((prevState) =>
                prevState.filter((account) => account.guid !== currentGuid)
            );
            setCurrentGuid('');
        };
        const errorHandler = () => {
            swal({
                text: t('Accounts.return.errorHandler.swal.text'),
                cancel: false,
                confirm: true,
                button: {
                    text: t('Accounts.return.errorHandler.swal.button'),
                    value: true,
                    visible: true,
                    className: 'btn btn-secondary',
                },
            });
        };

        fetchAccount(url, callBack, errorHandler);
    };
    const onArchiveAccount = () => {
        const currentAccount = [...accounts, ...archiveAccounts].find(
            (account) => account.guid === currentGuid
        );

        setIsShowPopup(false);
        loader.show();
        const url = helpable.replaceUrlParameters(routable.getRoute('archiveAccount'), {
            _guid_: currentGuid,
        });

        const callBack = (data) => {
            setAccounts((prevState) => {
                const filteredAccounts = prevState
                    .filter((account) => account.guid !== currentGuid)
                    .map((account) => {
                        if (account.guid === data.data?.default_account_guid) {
                            return { ...account, default_account: true };
                        }
                        return account;
                    });
                return filteredAccounts;
            });
            setArchiveAccounts((prevState) => [
                ...prevState,
                { ...currentAccount, is_archive: true, default_account: false },
            ]);
            setCurrentGuid('');
        };

        const errorHandler = (message) => {
            swal({
                title: t('Accounts.return.errorHandler.swal.title'),
                text: message,
                icon: 'warning',
                cancel: false,
                confirm: true,
                button: {
                    text: t('button.ok.text'),
                    value: true,
                    visible: true,
                    className: 'btn btn-light',
                },
            });
        };

        fetchArchiveAccount(url, callBack, errorHandler);
    };
    const onUnarchiveAccount = () => {
        const currentAccount = [...accounts, ...archiveAccounts].find(
            (account) => account.guid === currentGuid
        );

        setIsShowPopup(false);
        loader.show();
        const url = helpable.replaceUrlParameters(routable.getRoute('unArchiveAccount'), {
            _guid_: currentGuid,
        });
        const callBack = (data) => {
            const isDefaultAccount = currentAccount.guid === data.data?.default_account_guid;

            const unArchivedAccount = {
                ...currentAccount,
                is_archive: false,
                default_account: isDefaultAccount,
            };

            setArchiveAccounts((prevState) =>
                prevState.filter((account) => account.guid !== currentGuid)
            );
            setAccounts((prevState) => [...prevState, unArchivedAccount]);
            setCurrentGuid('');
        };

        const errorHandler = (message) => {
            swal({
                title: t('Accounts.return.onUnarchiveAccount.errorHandler.swal.title'),
                text: message,
                icon: 'warning',
                cancel: false,
                confirm: true,
                button: {
                    text: t('Accounts.return.errorHandler.swal.button'),
                    value: true,
                    visible: true,
                    className: 'btn btn-light',
                },
            });
        };
        fetchArchiveAccount(url, callBack, errorHandler);
    };

    const editPopupHandler = () => {
        loader.show();
        const currentAccount = [...accounts, ...archiveAccounts].find(
            (account) => account.guid === currentGuid
        );

        setIsShowPopup(false);

        const url = helpable.replaceUrlParameters(routable.getRoute('archiveAccount'), {
            _guid_: currentGuid,
        });
        const isDefaultAccount = accounts.some(
            (account) => account.guid === currentGuid && account.default_account
        );

        const errorHandler = (message) => {
            swal({
                title: t('Accounts.return.onUnarchiveAccount.errorHandler.swal.title'),
                text: message,
                icon: 'warning',
                cancel: false,
                confirm: true,
                button: {
                    text: t('button.ok.text'),
                    value: true,
                    visible: true,
                    className: 'btn btn-light',
                },
            });
        };

        const callBack = () => {
            setAccounts((prevState) => {
                const filteredAccounts = prevState.filter(
                    (account) => account.guid !== currentGuid
                );
                const newDefaultAccount = { ...filteredAccounts[0], default_account: true };

                if (isDefaultAccount) {
                    filteredAccounts.splice(0, 1, newDefaultAccount);
                }
                return filteredAccounts;
            });
            setArchiveAccounts((prevState) => [
                ...prevState,
                { ...currentAccount, is_archive: true, default_account: false },
            ]);
            setCurrentGuid('');
            setModalState((prevState) => ({
                ...prevState,
                currentAccount,
                isOpen: true,
                mode: 'add',
            }));
            loader.hide();
        };

        fetchArchiveAccount(url, callBack, errorHandler);
    };

    useEffect(() => {
        loader.show();
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
            },
        })
            .then((response) => response.json())
            .then((data) => {
                const getAccounts = data.data.filter((account) => account.is_archive === false);
                const getArchiveAccounts = data.data.filter(
                    (account) => account.is_archive === true
                );
                setAccounts(getAccounts);
                setArchiveAccounts(getArchiveAccounts);
                loader.hide();
            })
            .catch(() => {
                // TODO resolve catch
            });
    }, []);
    const popupMessageMapping = {
        remove: (
            <div style={{ textAlign: 'center', width: 553 }}>
                <Trans i18nKey="Accounts.return.popupMessageMapping.remove" />
            </div>
        ),
        archive: t('Accounts.return.popupMessageMapping.archive'),
        unarchive: <span>{t('Accounts.return.popupMessageMapping.unarchive')}</span>,
        edit: (
            <div>
                <div style={{ textAlign: 'center' }}>
                    {t('Accounts.return.popupMessageMapping.edit.part1')}
                    <br />
                    {t('Accounts.return.popupMessageMapping.edit.part2')}
                </div>
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                    {t('Accounts.return.popupMessageMapping.edit.part3')}
                    <br />
                    {t('Accounts.return.popupMessageMapping.edit.part4')}
                </div>
            </div>
        ),
    };
    const accountState = {
        accounts,
        setAccounts,
        archiveAccounts,
        setArchiveAccounts,
        activeMoreMenu,
        setActiveMoreMenu,
        isShowPopup,
        setIsShowPopup,
        popupMode,
        setPopupMode,
        currentGuid,
        setCurrentGuid,
        modalState,
        setModalState,
        currentList,
        employeelList,
        legalList,
        integrationList,
        setIntegrationList,
        isActiveSwitcherEdit,
        setIsActiveSwitcherEdit,
    };
    return (
        <Context.Provider value={accountState}>
            <div className={classNames(cls.wrapper)}>
                <div className={classNames(cls.addAccountBlock)}>
                    <UiButton
                        onClick={onAddAccountButton}
                        startIcon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12.2669 7.36686H8.63353V3.73353C8.63353 3.3839 8.34983 3.1002 8.0002 3.1002C7.65057 3.1002 7.36686 3.3839 7.36686 3.73353V7.36686H3.73353C3.3839 7.36686 3.1002 7.65057 3.1002 8.0002C3.1002 8.34982 3.3839 8.63353 3.73353 8.63353H7.36686V12.2669C7.36686 12.6165 7.65057 12.9002 8.0002 12.9002C8.34983 12.9002 8.63353 12.6165 8.63353 12.2669V8.63353H12.2669C12.6165 8.63353 12.9002 8.34982 12.9002 8.0002C12.9002 7.65057 12.6165 7.36686 12.2669 7.36686Z"
                                    fill="white"
                                    stroke="white"
                                    strokeWidth="0.2"
                                />
                            </svg>
                        }
                        btnText={t('Accounts.return.addAccountBlock.button.text')}
                    />
                </div>
                <h2 className={classNames(cls.header)}>{t('Accounts.return.header.text')}</h2>
                <div className={classNames(cls.tableWrapper)}>
                    <div>
                        <TableHead />
                        <AccountList accounts={accounts} />
                    </div>
                    <div>
                        <ArchiveAccounts accounts={archiveAccounts} />
                    </div>
                </div>
                <Popup
                    popupContentClassName={popupMode === 'remove' && cls.popup}
                    isOpen={isShowPopup}
                    onHidePopup={onHidePopup}
                    type="delete"
                    btnActionCb={
                        popupMode === 'remove'
                            ? popupFooterButtons.remove?.handler
                            : popupFooterButtons[popupMode]?.handler
                    }
                    deleteButtonText={
                        popupMode === 'remove'
                            ? popupFooterButtons.remove?.title
                            : popupFooterButtons[popupMode]?.title
                    }
                    cancelButtonText={t('Accounts.return.Popup.cancel.title')}
                    contentText={
                        typeof popupMessageMapping[popupMode] === 'string'
                            ? popupMessageMapping[popupMode]
                            : ' '
                    }
                >
                    {typeof popupMessageMapping[popupMode] !== 'string'
                        ? popupMessageMapping[popupMode]
                        : null}
                </Popup>

                {modalState.isOpen && (
                    <AccountModal
                        isOpen={modalState.isOpen}
                        mode={modalState.mode}
                        onShowModal={onHideModal}
                    />
                )}
            </div>
        </Context.Provider>
    );
};
